import { useUserPermissions } from '@/hooks/useUserPermissions';
import { MovableDialog } from '@/components/shared/MovableDialog';
import { useEffect, useState, type ReactElement } from 'react';
import {
  v2,
  type DocumentTemplate,
  type ProvenanceCategory,
  type CompanyProfile,
  type TemplateVariableProvenance,
  type VariableBindingApi,
  type VariableProvenance,
} from '@/api/v2Client';
import { formatApiError } from '@/lib/errors';
import { VariableBindingModal } from './VariableBindingModal';
import { BindingStatusBadge } from './BindingStatusBadge';

/**
 * phase-template-variable-provenance — where every variable in a template body
 * gets its value at generation time.
 *
 * Opened from the "N variables" cell on the Form Templates list. The data comes
 * from GET /document-templates/:id/variable-provenance, which walks the body's
 * tokens through the SAME resolvers generation uses (no forked resolution). We
 * only present the result here — grouped by source category, NO SOURCE rows
 * styled as warnings (they always generate as gaps; this doubles as authoring
 * lint). The visual precedent is the Company Profile field-usage popover.
 */
export interface TemplateProvenanceModalProps {
  readonly template: DocumentTemplate;
  readonly onClose: () => void;
}

// Display order: the resolvable sources first, NO SOURCE last and loud.
// phase-template-variable-bindings — `bound` sits first: a user-configured
// binding is the most specific answer to "where does this come from".
const CATEGORY_ORDER: readonly ProvenanceCategory[] = [
  'bound',
  'profile',
  'regionalContact',
  'submission',
  'computed',
  'none',
];

const CATEGORY_LABEL: Record<ProvenanceCategory, string> = {
  bound: 'Bound by you',
  profile: 'Company profile',
  regionalContact: 'Regional contact',
  submission: 'Submission',
  computed: 'System / computed',
  none: 'No source',
};

function TokenRow({
  v,
  onBind,
}: {
  v: VariableProvenance;
  onBind: (token: string) => void;
}): ReactElement {
  // A `none` row is a LOUD gap only when nothing resolves it (source === null).
  // A `none` row WITH a source is a static template default — real, not a gap.
  const isWarn = v.category === 'none' && v.source === null;
  // phase-template-variable-bindings — a binding pointing at something that no
  // longer resolves is its own loud state (design item 7).
  const hasIssue = typeof v.bindingIssue === 'string' && v.bindingIssue !== '';
  const loud = isWarn || hasIssue;
  return (
    <li
      data-testid={`provenance-row-${v.token}`}
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'baseline',
        padding: '6px 10px',
        borderRadius: 6,
        background: loud ? '#fef2f2' : 'transparent',
        border: loud ? '1px solid #fecaca' : '1px solid transparent',
        marginBottom: 4,
      }}
    >
      <code
        style={{
          fontFamily: 'monospace',
          fontSize: 12,
          fontWeight: 700,
          color: loud ? '#b91c1c' : 'var(--text)',
          whiteSpace: 'nowrap',
        }}
      >
        «{v.token}»
      </code>
      <span style={{ color: 'var(--text-muted)' }}>←</span>
      <span style={{ flex: 1, fontSize: 13, color: loud ? '#b91c1c' : 'var(--text-muted)' }}>
        {v.description}
        {v.isBound && (
          <span
            data-testid={`provenance-modified-${v.token}`}
            title="A binding overrides the built-in default for this variable."
            style={{ marginLeft: 6, color: '#b45309', fontWeight: 600 }}
          >
            ✎ modified from default
          </span>
        )}
        {hasIssue && (
          <span
            data-testid={`provenance-issue-${v.token}`}
            style={{ display: 'block', marginTop: 2, color: '#b91c1c', fontWeight: 600 }}
          >
            ⚠ {v.bindingIssue}
          </span>
        )}
        {/* Form Templates row 6 — whether it will actually get a value. */}
        {v.status && (
          <BindingStatusBadge
            testId={`provenance-status-${v.token}`}
            status={v.status}
            reason={v.reason}
            preview={v.resolvedPreview}
          />
        )}
        {/* Form Templates row 7 — every source, in precedence order. */}
        {(v.sources?.length ?? 0) > 1 && (
          <ol
            data-testid={`provenance-sources-${v.token}`}
            aria-label={`Sources for ${v.token}, in order of precedence`}
            style={{ margin: '4px 0 0', paddingLeft: 18, fontSize: 12 }}
          >
            {v.sources!.map((s) => (
              <li
                key={`${s.kind}-${s.rank}`}
                data-testid={`provenance-source-${v.token}-${s.rank}`}
                style={{
                  color: s.inEffect ? 'var(--text)' : 'var(--text-muted)',
                  textDecoration: s.inEffect ? 'none' : undefined,
                }}
              >
                {s.description}
                {s.value !== undefined && <span style={{ color: 'var(--text-muted)' }}> → {s.value}</span>}{' '}
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    padding: '0 6px',
                    borderRadius: 8,
                    background: s.inEffect ? '#dcfce7' : '#f1f5f9',
                    color: s.inEffect ? '#166534' : '#475569',
                  }}
                  title={s.note}
                >
                  {s.inEffect ? 'In effect' : s.kind === 'binding' || !v.isBound ? 'Fallback' : 'Overridden'}
                </span>
              </li>
            ))}
          </ol>
        )}
      </span>
      {/* design item 6 — the panel is the entry point. A NO SOURCE variable gets
          a prominent "Bind this variable"; everything else an unobtrusive edit. */}
      <button
        type="button"
        data-testid={`provenance-bind-btn-${v.token}`}
        onClick={() => onBind(v.token)}
        style={{
          flexShrink: 0,
          fontSize: 11,
          fontWeight: 600,
          borderRadius: 5,
          padding: isWarn ? '4px 10px' : '3px 8px',
          cursor: 'pointer',
          border: isWarn ? 'none' : '1px solid var(--border, #d1d5db)',
          background: isWarn ? '#b91c1c' : 'var(--surface, #fff)',
          color: isWarn ? '#fff' : 'var(--text-muted, #6b7280)',
        }}
      >
        {isWarn ? 'Bind this variable' : v.isBound ? 'Edit binding' : 'Bind'}
      </button>
    </li>
  );
}

function CategoryGroup({
  category,
  rows,
  onBind,
}: {
  category: ProvenanceCategory;
  rows: VariableProvenance[];
  onBind: (token: string) => void;
}): ReactElement | null {
  if (rows.length === 0) return null;
  // Warn-style the group header only when it contains a true gap (a `none` row
  // with no source); a group of default-backed rows is not a warning.
  const isWarn = category === 'none' && rows.some((r) => r.source === null);
  return (
    <section data-testid={`provenance-group-${category}`} style={{ marginBottom: 18 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.6,
          textTransform: 'uppercase',
          color: isWarn ? '#b91c1c' : 'var(--text-muted)',
          marginBottom: 8,
        }}
      >
        {isWarn ? '⚠ ' : ''}
        {CATEGORY_LABEL[category]} ({rows.length})
      </div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {rows.map((v) => (
          <TokenRow key={v.token} v={v} onBind={onBind} />
        ))}
      </ul>
    </section>
  );
}

export function TemplateProvenanceModal({
  template,
  onClose,
}: TemplateProvenanceModalProps): ReactElement {
  const permissions = useUserPermissions();
  const canReadProfiles = permissions.hasPermission('CompanyProfiles', 'read');
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileReload, setProfileReload] = useState(0);
  const [data, setData] = useState<TemplateVariableProvenance | null>(null);
  const [error, setError] = useState<string | null>(null);
  // phase-template-variable-bindings — the tenant's bindings for this template,
  // so the editor opens pre-filled and Reset is offered where one is in force.
  const [bindings, setBindings] = useState<VariableBindingApi[]>([]);
  const [bindingTarget, setBindingTarget] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // phase-reconcile-contact-bindings — resolving CONTACT/SIGNATORY bindings
  // needs a real profile; with one chosen each bound row reports WHICH step of
  // the resolution chain matched (an exact function match vs the region's
  // primary vs an all-regions fallback). Without one the panel stays static and
  // says nothing about the step rather than guessing.
  const [profiles, setProfiles] = useState<CompanyProfile[]>([]);
  const [resolveAsId, setResolveAsId] = useState<string>('');

  useEffect(() => {
    if (!canReadProfiles) return;
    let cancelled = false;
    setProfileError(null);
    v2.companyProfiles
      .list()
      .then((res) => {
        if (!cancelled) setProfiles(Array.isArray(res) ? res : []);
      })
      .catch((reason: unknown) => {
        if (!cancelled) { setProfiles([]); setProfileError(formatApiError(reason, 'Could not load company profiles.')); }
      });
    return (): void => {
      cancelled = true;
    };
  }, [canReadProfiles, profileReload]);

  const activeProfileId = canReadProfiles ? resolveAsId : '';
  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    v2.documentTemplates
      .variableProvenance(template.Id, activeProfileId === '' ? undefined : activeProfileId)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(formatApiError(e, 'Could not load variable sources.'));
      });
    // Bindings are a separate read; a failure here must not blank the panel.
    v2.documentTemplates
      .variableBindings(template.Id)
      .then((res) => {
        if (!cancelled) setBindings(Array.isArray(res?.bindings) ? res.bindings : []);
      })
      .catch(() => {
        if (!cancelled) setBindings([]);
      });
    return (): void => {
      cancelled = true;
    };
  }, [template.Id, reloadKey, activeProfileId]);

  const regionLabel = data
    ? data.isGlobal
      ? 'All regions'
      : (data.region ?? 'All regions')
    : (template.Region ?? null);

  // A stored count that disagrees with the walk is stale metadata — trust the
  // walk and say so.
  const countMismatch =
    data && data.storedCount !== null && data.storedCount !== data.tokenCount;

  // phase-movable-dialogs — shared movable shell.
  return (
    <MovableDialog
      persistKey="formTemplates.provenance"
      title={
        <span id="template-provenance-title">
          {template.Name}
          <span style={{ display: 'block', fontSize: 12, fontWeight: 400, color: 'var(--text-muted)' }}>
            Variable sources{regionLabel ? ` · ${regionLabel}` : ''}
          </span>
        </span>
      }
      ariaLabelledBy="template-provenance-title"
      testId="template-provenance-modal"
      onClose={onClose}
      closeOnBackdrop
      width="min(640px, 92vw)"
      maxHeight="85vh"
      zIndex={1000}
    >
      <>

        <div style={{ padding: 20, overflowY: 'auto' }}>
          {error && (
            <div role="alert" style={{ color: '#b91c1c', fontSize: 13 }}>
              {error}
            </div>
          )}
          {!error && !data && (
            <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>
          )}
          {data && (
            <>
              <div
                data-testid="provenance-count"
                style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}
              >
                {data.tokenCount} variable{data.tokenCount === 1 ? '' : 's'} in this template.
                {countMismatch && (
                  <span style={{ color: '#b45309' }}>
                    {' '}
                    (stored count says {data.storedCount} — showing the true count from the body.)
                  </span>
                )}
              </div>

              {/* phase-reconcile-contact-bindings — a contact binding's step is
                  only knowable against a real profile. Choose one and each bound
                  row says how it resolved. */}
              <div style={{ marginBottom: 16, fontSize: 13 }}>
                <label htmlFor="provenance-resolve-as" style={{ color: 'var(--text-muted)' }}>
                  Check against profile{' '}
                </label>
                <select
                  id="provenance-resolve-as"
                  data-testid="provenance-resolve-as"
                  value={activeProfileId}
                  disabled={!canReadProfiles}
                  onChange={(e) => setResolveAsId(e.target.value)}
                  style={{
                    padding: '4px 8px',
                    border: '1px solid var(--border)',
                    borderRadius: 4,
                    fontSize: 13,
                    background: 'var(--surface)',
                    color: 'var(--text)',
                  }}
                >
                  <option value="">(no profile — values not checked)</option>
                  {profiles.map((p) => (
                    <option key={p.Id} value={p.Id}>
                      {p.ProfileName ?? p.CompanyName}
                    </option>
                  ))}
                </select>
                {!permissions.loading && !canReadProfiles && <p>Company-profile access is needed to check real values. Variable sources remain available.</p>}
                {profileError && <p role="alert">{profileError} <button type="button" onClick={() => setProfileReload(n => n + 1)}>Retry profiles</button></p>}
              </div>

              {data.variables.length === 0 ? (
                <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                  This template has no variable placeholders.
                </div>
              ) : (
                CATEGORY_ORDER.map((cat) => (
                  <CategoryGroup
                    key={cat}
                    category={cat}
                    rows={data.variables.filter((v) => v.category === cat)}
                    onBind={setBindingTarget}
                  />
                ))
              )}
            </>
          )}
        </div>

        {/* phase-template-variable-bindings — the binding editor, opened from a
            row. On save/reset we bump reloadKey so the panel re-walks and the
            variable's category updates to its bound source (design item 6). */}
        {bindingTarget !== null && (
          <VariableBindingModal
            templateId={template.Id}
            variableName={bindingTarget}
            existing={bindings.find((b) => b.variableName === bindingTarget) ?? null}
            // Row 6 — the editor's live check runs against the same profile.
            profileId={activeProfileId === '' ? null : activeProfileId}
            onClose={() => setBindingTarget(null)}
            onSaved={() => {
              setBindingTarget(null);
              setReloadKey((k) => k + 1);
            }}
          />
        )}

        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            data-testid="provenance-close-btn"
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 4,
              padding: '6px 14px',
              cursor: 'pointer',
              fontSize: 13,
            }}
          >
            Close
          </button>
        </div>
      </>
    </MovableDialog>
  );
}
